import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { addDoc, collection, doc, getDoc, updateDoc } from "firebase/firestore";
import { firestore, auth } from "../shared/FirebaseConfig";
import { UserContext } from "../shared/UserContext";
import StudentIdSetupModal, { useStudentIdSetup } from "./StudentIdSetupModal";
import { createChecklist } from "../shared/checklistData";

const GRADE_LEVELS = [
  "Pre-K", "Kindergarten",
  "1st", "2nd", "3rd", "4th", "5th",
  "6th", "7th", "8th",
  "9th", "10th", "11th", "12th",
  "Mixed",
];

const currentYear = new Date().getFullYear();

const AddClass = () => {
  const { uid, schoolId, studentIdScheme } = useContext(UserContext);
  const navigate = useNavigate();
  const { classId } = useParams();
  const isEditing = Boolean(classId);

  const [className, setClassName] = useState("");
  const [gradeLevel, setGradeLevel] = useState("");
  const [schoolYear, setSchoolYear] = useState(`${currentYear}-${currentYear + 1}`);
  const [roomNumber, setRoomNumber] = useState("");
  // A "specials / pull-out" class (speech, band, resource, nurse…) holds kids who
  // belong to another teacher's homeroom. It never becomes a student's homeroom or
  // their emergency-accounting group when they already have a real one, and its
  // students are always written to the shared directory (never private duplicates)
  // so accounting for a kid checks them off the master list too. See classType usage
  // in shared/activeClass.js + AddStudent.js.
  const [isSpecials, setIsSpecials] = useState(false);
  const [error, setError] = useState("");
  // After a teacher titles + creates their first class, ask how they identify
  // students (sets up sign-out) before sending them on to add students. Skipped
  // once a scheme is chosen or deferred. Holds the new class id until answered.
  const { showIdSetup, deferIdSetup } = useStudentIdSetup(uid, studentIdScheme);
  const [createdClassId, setCreatedClassId] = useState(null);

  // Land on the new class's Add-Students screen with the import options open.
  const goToAddStudents = (newClassId) =>
    navigate("/displayenrolledstudents", { state: { classId: newClassId, openAdd: true } });

  // Editing — load the existing class into the form.
  useEffect(() => {
    if (!classId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, "classes", classId));
        if (snap.exists()) {
          const d = snap.data();
          setClassName(d.className || "");
          setGradeLevel(d.gradeLevel || "");
          setSchoolYear(d.schoolYear || `${currentYear}-${currentYear + 1}`);
          setRoomNumber(d.roomNumber || "");
          setIsSpecials(d.classType === "specials");
        }
      } catch (err) {
        console.error("Error loading class:", err);
      }
    })();
  }, [classId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const classType = isSpecials ? "specials" : "homeroom";
      if (isEditing) {
        await updateDoc(doc(firestore, "classes", classId), {
          className, gradeLevel, schoolYear, roomNumber, classType,
          // A specials class can never also be the flagged homeroom.
          ...(isSpecials ? { isHomeroom: false } : {}),
        });
        navigate("/dashboard");
        return;
      }

      const ref = await addDoc(collection(firestore, "classes"), {
        className,
        gradeLevel,
        schoolYear,
        roomNumber,
        classType,
        teacherId: auth.currentUser.uid,
        schoolId,
      });

      // Seed ONE default checklist for the new class (renameable/deletable after).
      // Done here at creation — never re-created — so deleting it later sticks.
      createChecklist({ ownerUid: uid, schoolId, classId: ref.id, title: "Checklist" })
        .catch((e) => console.error("Error seeding default checklist:", e));

      // First class with no ID scheme yet → ask before moving on. Otherwise go
      // straight to adding students for the class we just created.
      if (showIdSetup) {
        setCreatedClassId(ref.id);
      } else {
        goToAddStudents(ref.id);
      }
    } catch (err) {
      console.error("Error saving class:", err);
      setError(isEditing ? "Failed to update class. Please try again." : "Failed to create class. Please try again.");
    }
  };

  return (
    <div className="form-container">
      {createdClassId && (
        <StudentIdSetupModal
          uid={uid}
          onClose={() => {
            deferIdSetup();
            goToAddStudents(createdClassId);
          }}
        />
      )}
      <h2>{isEditing ? "Edit Class" : "Add New Class"}</h2>
      <form onSubmit={handleSubmit}>
        <div>
          <label>Class Name</label>
          <input
            type="text"
            value={className}
            onChange={(e) => setClassName(e.target.value)}
            placeholder="e.g. Period 3 English"
            required
          />
        </div>
        <div>
          <label>Grade Level</label>
          <select
            value={gradeLevel}
            onChange={(e) => setGradeLevel(e.target.value)}
            required
          >
            <option value="">Select grade</option>
            {GRADE_LEVELS.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>
        <div>
          <label>School Year</label>
          <input
            type="text"
            value={schoolYear}
            onChange={(e) => setSchoolYear(e.target.value)}
            required
          />
        </div>
        <div>
          <label>Room Number</label>
          <input
            type="text"
            value={roomNumber}
            onChange={(e) => setRoomNumber(e.target.value)}
            placeholder="e.g. 204 (optional)"
          />
        </div>
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 12, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={isSpecials}
            onChange={(e) => setIsSpecials(e.target.checked)}
            style={{ width: "auto", marginTop: 3 }}
          />
          <span style={{ fontWeight: 400 }}>
            <strong>This is a specials / pull-out class</strong> (speech, band, resource, nurse…)
            — for kids who belong to another teacher's homeroom. It <strong>won't change the
            master rosters</strong>, but accounting for a student here (e.g. in an emergency)
            still checks them off their homeroom list.
          </span>
        </label>
        {error && <p className="error-message">{error}</p>}
        <button type="submit">{isEditing ? "Save Changes" : "Create Class"}</button>
      </form>
      {!isEditing && (
        <p style={{ marginTop: 16, fontSize: 14, color: "var(--color-secondary)" }}>
          Have a roster? <Link to="/importclasses">Bulk import students &amp; classes</Link>{" "}
          — paste one list and it creates all your classes at once.
        </p>
      )}
    </div>
  );
};

export default AddClass;
